import { useEffect, useRef } from 'react';

interface ShaderBackgroundProps {
  active: boolean;
  height?: number;
  analyser?: AnalyserNode | null;
  isDark?: boolean;
  className?: string;
}

const HISTORY_SIZE = 2;
const SAMPLE_POINTS = 128;
const CAPTURE_INTERVAL = 3;

function downsample(data: Uint8Array, points: number): number[] {
  const step = data.length / points;
  const out: number[] = [];
  for (let i = 0; i < points; i++) {
    const idx = Math.floor(i * step);
    out.push((data[idx] - 128) / 128);
  }
  return out;
}

export function ShaderBackground({ active, height = 200, analyser, isDark = true, className }: ShaderBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const activeRef = useRef(active);
  const analyserRef = useRef(analyser);
  const isDarkRef = useRef(isDark);
  const historyRef = useRef<number[][]>([]);
  const frameCountRef = useRef(0);
  const timeDomainRef = useRef<Uint8Array | null>(null);

  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { analyserRef.current = analyser; }, [analyser]);
  useEffect(() => { isDarkRef.current = isDark; }, [isDark]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const render = () => {
      const c = canvasRef.current;
      const cx = c?.getContext('2d');
      if (!c || !cx) return;

      const dpr = window.devicePixelRatio || 1;
      const w = c.clientWidth;
      const h = c.clientHeight;
      if (c.width !== w * dpr || c.height !== h * dpr) {
        c.width = w * dpr;
        c.height = h * dpr;
      }
      cx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx.clearRect(0, 0, w, h);

      const an = analyserRef.current;
      if (activeRef.current && an) {
        if (!timeDomainRef.current || timeDomainRef.current.length !== an.fftSize) {
          timeDomainRef.current = new Uint8Array(an.fftSize);
        }
        an.getByteTimeDomainData(timeDomainRef.current);

        frameCountRef.current++;
        if (frameCountRef.current % CAPTURE_INTERVAL === 0) {
          const samples = downsample(timeDomainRef.current, SAMPLE_POINTS);
          historyRef.current.push(samples);
          if (historyRef.current.length > HISTORY_SIZE) {
            historyRef.current.shift();
          }
        }
      } else if (!activeRef.current && historyRef.current.length > 0) {
        historyRef.current.shift();
      }

      const history = historyRef.current;
      if (history.length === 0) {
        animRef.current = requestAnimationFrame(render);
        return;
      }

      const dark = isDarkRef.current;
      const midY = h / 2;
      const amplitude = h * 0.54;

      for (let hi = 0; hi < history.length; hi++) {
        const samples = history[hi];
        const age = 1 - hi / history.length;
        const freshness = hi / (history.length - 1 || 1);

        const baseAlpha = 0.06 + freshness * 0.55;
        const glowWidth = 3 + freshness * 4;
        const coreWidth = 0.8 + freshness * 1.5;

        const yOffset = (age - 0.5) * h * 0.12 * Math.sin(hi * 1.1);

        const r = dark ? 255 : 224;
        const g = dark ? 0 + Math.round(freshness * 30) : 0;
        const b = dark ? 94 - Math.round(age * 30) : 90;

        // Horizontal fade gradient -- lines fade at edges
        const grad = cx.createLinearGradient(0, 0, w, 0);
        grad.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`);
        grad.addColorStop(0.1, `rgba(${r}, ${g}, ${b}, ${baseAlpha})`);
        grad.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${baseAlpha})`);
        grad.addColorStop(0.9, `rgba(${r}, ${g}, ${b}, ${baseAlpha})`);
        grad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

        const glowGrad = cx.createLinearGradient(0, 0, w, 0);
        const glowAlpha = baseAlpha * 0.3;
        glowGrad.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`);
        glowGrad.addColorStop(0.1, `rgba(${r}, ${g}, ${b}, ${glowAlpha})`);
        glowGrad.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${glowAlpha})`);
        glowGrad.addColorStop(0.9, `rgba(${r}, ${g}, ${b}, ${glowAlpha})`);
        glowGrad.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

        // Build smooth bezier path
        cx.beginPath();
        for (let i = 0; i < samples.length; i++) {
          const x = (i / (samples.length - 1)) * w;
          const y = midY + samples[i] * amplitude + yOffset;
          if (i === 0) {
            cx.moveTo(x, y);
          } else {
            const prevX = ((i - 1) / (samples.length - 1)) * w;
            const prevY = midY + samples[i - 1] * amplitude + yOffset;
            const cpX = (prevX + x) / 2;
            cx.quadraticCurveTo(cpX, prevY, (cpX + x) / 2, (prevY + y) / 2);
          }
        }

        // Glow pass
        cx.strokeStyle = glowGrad;
        cx.lineWidth = glowWidth;
        cx.lineCap = 'round';
        cx.lineJoin = 'round';
        cx.stroke();

        // Core pass
        cx.strokeStyle = grad;
        cx.lineWidth = coreWidth;
        cx.stroke();
      }

      animRef.current = requestAnimationFrame(render);
    };

    animRef.current = requestAnimationFrame(render);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        height,
        opacity: active ? 1 : 0,
        transition: 'opacity 0.5s ease',
      }}
    />
  );
}

export default ShaderBackground;
